import React, { useState, useEffect, useCallback } from 'react';
import { getMyReports } from '../../../api/services/reportService';
import { IoAlertCircleOutline, IoCheckmarkCircleOutline, IoTimeOutline, IoChevronBack, IoChevronForward, IoChatboxEllipsesOutline } from 'react-icons/io5';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import './MyReports.css';

const MyReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [totalPages, setTotalPages] = useState(0);

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getMyReports({ page, size });

      if (response.status === 'success' && response.data) {
        setReports(response.data.content || []);
        setTotalPages(response.data.totalPages || 1);
      }
    } catch (err) {
      setError(err.message || 'Failed to load your reports');
      console.error('Error loading my reports:', err);
    } finally {
      setLoading(false);
    }
  }, [page, size]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, { 
        year: 'numeric', 
        month: 'short', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'OPEN':
        return <span className="report-badge status-open"><IoTimeOutline /> Open</span>;
      case 'RESOLVED':
        return <span className="report-badge status-resolved"><IoCheckmarkCircleOutline /> Resolved</span>;
      case 'CLOSED':
        return <span className="report-badge status-closed"><IoAlertCircleOutline /> Closed</span>;
      default:
        return <span className="report-badge">{status}</span>;
    }
  };

  const getCategoryBadge = (category) => {
    return <span className="report-category-tag">{category.replace(/_/g, ' ')}</span>;
  };

  return (
    <div className="my-reports-container">
      <CollapsibleTopPanel id="my-reports-header-collapsible" defaultOpenOnMobile={false}>
        <div className="reports-header-section">
          <div>
            <h1 className="page-title">My Reports</h1>
            <p className="page-subtitle">Track the status of issues you have reported</p>
          </div>
        </div>
      </CollapsibleTopPanel>

      {loading ? (
        <div className="reports-loading-state">
          <div className="spinner"></div>
          <p>Fetching your reports...</p>
        </div>
      ) : error ? (
        <div className="reports-error-state">
          <IoAlertCircleOutline size={40} />
          <p>{error}</p>
          <button onClick={loadReports}>Retry</button>
        </div>
      ) : reports.length === 0 ? (
        <div className="reports-empty-state">
          <IoChatboxEllipsesOutline size={40} />
          <p>You haven't submitted any reports yet.</p>
        </div>
      ) : (
        <div className="reports-table-wrapper">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Issue Details</th>
                <th>Category</th>
                <th>Property</th>
                <th>Submitted On</th>
                <th>Status</th>
                <th>Resolution</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.id} className="report-row">
                  <td className="report-main-cell" data-label="Issue Details">
                    <div className="report-title-text">{report.title}</div>
                    <div className="report-desc-preview">{report.description}</div>
                  </td>
                  <td data-label="Category">{getCategoryBadge(report.category)}</td>
                  <td data-label="Property">
                    <div className="report-context">
                      <strong>{report.propertyName || 'N/A'}</strong>
                      {report.unitNumber && <span className="unit-label">Unit {report.unitNumber}</span>}
                    </div>
                  </td>
                  <td className="date-cell" data-label="Submitted On">{formatDate(report.createdAt)}</td>
                  <td data-label="Status">{getStatusBadge(report.status)}</td>
                  <td data-label="Resolution">
                    {report.status === 'RESOLVED' ? (
                      <div className="res-notes-container" title={report.resolutionNotes}>
                         <div className="res-notes-label">Notes:</div>
                         <div className="res-notes-text">{report.resolutionNotes || 'Problem resolved'}</div>
                      </div>
                    ) : (
                      <span className="pending-text">Awaiting review</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination-controls">
          <button 
            disabled={page === 0} 
            onClick={() => setPage(p => p - 1)}
            className="pagination-btn"
          >
            <IoChevronBack /> Prev
          </button>
          <span className="page-info">Page {page + 1} of {totalPages}</span>
          <button 
            disabled={page >= totalPages - 1} 
            onClick={() => setPage(p => p + 1)}
            className="pagination-btn"
          >
            Next <IoChevronForward />
          </button>
        </div>
      )}
    </div>
  );
};

export default MyReports;
